import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {View, Platform, PixelRatio, StyleSheet} from 'react-native';

import {COMMAND_ROUTE, USER_ROUTE, VIEWBOARD_SYNC} from 'utils/World/config';
import PlatformWorld from 'components/World/PlatformWorld';
import Loading from 'components/World/Loading';
import TextOverlayManager from 'components/World/TextOverlayManager';
import LabelDisplay from 'components/World/LabelDisplay';

class World extends Component {
  static instance = undefined;
  constructor(props) {
    super(props);
    this.state = {loading: true, lowPower: false};
  }

  componentDidMount() {
    if (World.instance) {
      throw new Error('Cannot have multiple 3d world components mounted');
    }
    World.instance = this;
  }

  componentWillUnmount() {
    World.instance = undefined;
  }

  shouldComponentUpdate(nextProps) {
    const {location} = this.props;
    if (nextProps.location !== location) {
      console.log('[WEBGL_DBG] World location changed: ' + this.props.location + ' -> ' + nextProps.location);
      this.setState({loading: true});
    }

    if (nextProps.lowPower !== this.state.lowPower) {
      this.setPowerMode(nextProps.lowPower);
    }

    return true;
  }

  static runCommand = (command) => {
    console.log('COMMAND ROUTE: ', COMMAND_ROUTE);
    console.log('COMMAND: ', command);

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: command,
    });
  };

  static onReceivePeerMessage = (message) => {
    if (!message.payload || !message.payload.includes('transform|')) {
      console.log('[MP_RN] World.onReceivePeerMessage: route=' + message.route + ' peer=' + message.peer + ' payloadLen=' + (message.payload ? message.payload.length : 0));
    }
    if (message.route === 'j' || message.route === USER_ROUTE) {
      message.payload = `${message.peer}|${message.payload}`;
      PlatformWorld.sendMessage(message);
    } else if (message.action === 'teleport') {
      World.runCommand(`player_leave ${message.peer}`);
    } else if (message.route === VIEWBOARD_SYNC) {
      PlatformWorld.sendMessage(message);
    }
  };

  static onReceivePeerState = (peerName, state) => {
    console.log('[MP_RN] World.onReceivePeerState: peerId=' + peerName + ' avatarId=' + (state && state.avatarId) + ' skinTone=' + (state && state.skinToneHex));
    if (
      state &&
      state.avatarId &&
      state.skinToneHex
    ) {
      // Unity Communication.PeerAppearance expects: `peerId|<base64-encoded AvatarInfo JSON>`
      // Field names must match Unity's AvatarInfo C# class exactly: avaTwinAvatarId, avaTwinSkinToneHex
      const avatarInfoJson = JSON.stringify({
        avaTwinAvatarId: state.avatarId,
        avaTwinSkinToneHex: state.skinToneHex,
      });
      const encoded = global.btoa(unescape(encodeURIComponent(avatarInfoJson)));
      console.log('[MP_RN] World -> PlatformWorld.sendMessage: route=d peer=' + peerName + ' encodedLen=' + encoded.length);
      PlatformWorld.sendMessage({
        route: 'd',
        payload: `${peerName}|${encoded}`,
      });
    }
  };

  static onUpdatePeerFloatingName = (peerId, peerDisplayName) => {
    if (peerId && peerDisplayName) {
      PlatformWorld.sendMessage({
        route: USER_ROUTE,
        payload: `${peerId}|nickname|${peerDisplayName}`,
      });
    }
  };

  filterTransformMessages = (message) => {
    console.log('[WEBGL_DBG] World.filterTransformMessages received');
    const {onTransformFromWorld} = this.props;
    const t = message.value;
    const values = `${t.x} ${t.y} ${t.z} ${t.rx} ${t.ry} ${t.rz} ${t.time}`;
    const payload = `transform|${values}|${t.filter}`;

    onTransformFromWorld({route: USER_ROUTE, payload});
  };

  filterLegacyMessages = (message) => {
    console.log('[WEBGL_DBG] World.filterLegacyMessages route=' + (message && message.route));
    const {onLegacyFromWorld} = this.props;
    const {route, payload} = message;

    if (route === 'x') {
      console.log('[WEBGL_DBG] World: legacy route=x level-load-complete');
      this.onLevelLoadComplete();
    } else if (route === 'p') {
      // ignore the old/erroneous command that sends a number with the p. In that case the payload will be undefined.
      if (!payload) {
        return;
      }
      const player_variables = payload.split('|');
      if (player_variables[0] === 'avatarReady') {
        console.log('[WEBGL_DBG] World: legacy route=p avatarReady; hiding loading overlay');
        // Unity signals that an avatar has finished loading (post-save,
        // full AvatarManager pipeline). Hide the Loading overlay if it's
        // still up (e.g. meeting/house scene where customizerReady isn't
        // emitted).
        // Payload format: avatarReady|<avatarId>|<skinToneHex>
        this.setState({loading: false});
        onLegacyFromWorld(message);
      } else if (player_variables[0] === 'customizerReady') {
        console.log('[WEBGL_DBG] World: legacy route=p customizerReady; hiding loading overlay');
        // Ava-Twin customize scene: Unity emits this the moment the preview
        // GLB is visible on screen (CharacterLoader.CharacterVisualLoaded).
        // Equivalent in spirit to the old `updatedAppearance` signal — the
        // user has something to look at, uncover the Loading overlay so
        // they can see and tap the in-scene Canvas UI.
        this.setState({loading: false});
      }
    } else {
      console.warn('[WEBGL_DBG] World: legacy route unhandled route=' + (message && message.route) + ' payload=' + (message && message.payload));
      onLegacyFromWorld(message);
    }
  };

  setPowerMode(lowPower) {
    if (lowPower) {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_quality LOW',
      });
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_fps_limit 24',
      });
    } else {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_quality HIGH',
      });
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_fps_limit 30',
      });
    }

    this.setState({lowPower: lowPower});
  }

  onLevelLoadComplete = () => {
    console.log('[WEBGL_DBG] World.onLevelLoadComplete: sending scene config commands');
    const {
      locationFilter,
      onLoadComplete,
      lowPower,
      showFloatingNames,
    } = this.props;

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: `set_location_filter ${locationFilter}`,
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'finish_loading',
    });
    PlatformWorld.sendMessage({route: COMMAND_ROUTE, payload: 'after_join'});
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: `show_names ${showFloatingNames}`,
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'show_chat false',
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'enable_movement true',
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'use_player_camera',
    });

    this.setPowerMode(lowPower);

    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'show_thumbstick false',
      });

      const thumbSize = PixelRatio.getPixelSizeForLayoutSize(56);
      const thumbTravel = PixelRatio.getPixelSizeForLayoutSize(112);
      const thumbX = PixelRatio.getPixelSizeForLayoutSize(84);
      const thumbY = PixelRatio.getPixelSizeForLayoutSize(160);
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: `set_thumbstick ${thumbX} ${thumbY} ${thumbSize} ${thumbTravel}`,
      });
    }
    onLoadComplete();
  };

  render() {
    const {location, style} = this.props;
    const {loading} = this.state;

    return (
      <View pointerEvents="box-none" style={[styles.background, style]}>
        {location && (
          <PlatformWorld
            location={location}
            onTransform={(message) => this.filterTransformMessages(message)}
            onLegacyMessage={(message) => this.filterLegacyMessages(message)}
          />
        )}
        <Loading loading={loading} />
        <TextOverlayManager loading={loading} />
        <LabelDisplay loading={loading} />
      </View>
    );
  }
}

World.defaultProps = {
  style: {},
  onTransformFromWorld: () => {},
  onLegacyFromWorld: () => {},
  onLoadComplete: () => {},
  lowPower: false,
  showFloatingNames: false,
};

World.propTypes = {
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number,
    PropTypes.array,
  ]),
  location: PropTypes.string.isRequired,
  locationFilter: PropTypes.string.isRequired,
  onTransformFromWorld: PropTypes.func,
  onLegacyFromWorld: PropTypes.func,
  onLoadComplete: PropTypes.func,
  lowPower: PropTypes.bool,
  showFloatingNames: PropTypes.bool,
};

const styles = StyleSheet.create({
  background: {
    backgroundColor: '#000',
  },
});

export default World;
